import { _ } from "../../../polyglot/polyglot.js";
import { titleRenderer } from "../../makeRoadmapStepContent.js";
import AbstractPayablesAndReceivablesStep from "./AbstractPayablesAndReceivablesStep.js";
import AccountGroupSetterLineWidget from "./AccountGroupSetterLineWidget/AccountGroupSetterLineWidget.js";
import regionWidget from "../../../widgets/regionWidget.js";
import TextRepresentationAccountSpecificationVisitor from "../../../../models/accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";

/**
 * This allows the user to choose account and delay for
 * account payable and account receivable.
 */
export default class AccountsPayableAndReceivableStep extends AbstractPayablesAndReceivablesStep {
	getTitle() {
		return _("Existing payables and receivables");
	}

	_doStart() {
		if (this._getAccountsReceivableString() === "")
			this._setExistingAccountsReceivable(
				this._context.configuration.getForecastAccountsReceivable(),
			);

		if (this._getExistingAccountsPayableString() === "")
			this._setExistingAccountsPayable(
				this._context.configuration.getForecastAccountsPayable(),
			);

		if (!this._isValidDelays(this._getReceivableDelays()))
			this._setReceivableDelays(
				this._context.configuration.getForecastAccountsReceivableDelays(),
			);

		if (!this._isValidDelays(this._getPayableDelays()))
			this._setPayableDelays(
				this._context.configuration.getForecastAccountsPayableDelays(),
			);
	}

	_setExistingAccountsReceivable(value) {
		this._context.configuration.setExistingAccountsReceivable(value);
		const string =
			new TextRepresentationAccountSpecificationVisitor().visit(value);
		this._accountsReceivableGroups?.setContent(string);
	}

	_setExistingAccountsPayable(value) {
		this._context.configuration.setExistingAccountsPayable(value);
		const string =
			new TextRepresentationAccountSpecificationVisitor().visit(value);
		this._accountsPayableGroups?.setContent(string);
	}

	_getReceivableDelays() {
		return this._context.configuration.getExistingAccountsReceivableDelays();
	}

	_setReceivableDelays(value) {
		this._context.configuration.setExistingAccountsReceivableDelays(value);
	}

	_getPayableDelays() {
		return this._context.configuration.getExistingAccountsPayableDelays();
	}

	_setPayableDelays(value) {
		this._context.configuration.setExistingAccountsPayableDelays(value);
	}

	_isValidDelays(delays) {
		if (!delays?.length) return false;

		const total = delays.reduce((delay, sum) => sum + delay, 0);

		return total === 100;
	}

	_getSubtitle() {
		return _(
			"In your existing bookkeeping you may have some outstanding receivable and payable invoices, these will be paid in coming periods and will affect the bank balance, so please select these accounts.",
		);
	}

	_getStepContent() {
		return (html) => {
			html.div(
				{ class: "accounts-receivable" },
				titleRenderer(_("Accounts receivable")),
				html.div(
					{ class: "actions" },
					this._accountsReceivableGroupsRenderer(),
					this._accountsReceivableDelaysRenderer(),
				),
			);

			html.div(
				{ class: "accounts-payable" },
				titleRenderer(_("Accounts payable")),
				html.div(
					{ class: "actions" },
					this._accountsPayableGroupsRenderer(),
					this._accountsPayableDelaysRenderer(),
				),
			);
		};
	}

	_accountsReceivableGroupsRenderer() {
		return new AccountGroupSetterLineWidget({
			title: _("Accounts"),
			titleCssClass: "action-label",
			helpText: _(
				"Choose which asset accounts you record unpaid customer invoices to in your bookkeeping software.",
			),
			dataTestName: "accounts-receivable-groups",
			inputName: _("Intervals"),
			accessor: () =>
				this._context.configuration.getExistingAccountsReceivable(),
			mutator: (values) =>
				this._context.configuration.setExistingAccountsReceivable(
					values,
				),
		});
	}

	_accountsReceivableDelaysRenderer() {
		this._receivableDelays ??= regionWidget({
			content: this._formatDelays(this._getReceivableDelays()),
		});

		return this._actionRenderer({
			label: _("Delay settings"),
			value: this._receivableDelays,
			dataTestName: "accounts-receivable-delays",
			linkLabel: _("+ select delays"),
			helpText: _(
				"Choose which percentage of actual customer invoices you expect to be paid in each following month. It must add up to 100%",
			),
			click: () => this._getReceivableDelayDialog().show(),
		});
	}

	_accountsPayableGroupsRenderer() {
		return new AccountGroupSetterLineWidget({
			title: _("Accounts"),
			titleCssClass: "action-label",
			helpText: _(
				"Choose which liability accounts you record your unpaid supplier invoices to in your bookkeeping software.",
			),
			dataTestName: "accounts-payable-groups",
			inputName: _("Intervals"),
			accessor: () =>
				this._context.configuration.getExistingAccountsPayable(),
			mutator: (values) =>
				this._context.configuration.setExistingAccountsPayable(values),
		});
	}

	_accountsPayableDelaysRenderer() {
		this._payableDelays ??= regionWidget({
			content: this._formatDelays(this._getPayableDelays()),
		});

		return this._actionRenderer({
			label: _("Delay settings"),
			value: this._payableDelays,
			dataTestName: "accounts-payable-delays",
			linkLabel: _("+ select delays"),
			helpText: _(
				"Choose which percentage of actual supplier invoices you expect to pay in each following month. It must add up to 100%",
			),
			click: () => this._getPayableDelayDialog().show(),
		});
	}

	_getAccountsReceivableString() {
		return new TextRepresentationAccountSpecificationVisitor().visit(
			this._context.configuration.getExistingAccountsReceivable(),
		);
	}

	_getExistingAccountsPayableString() {
		return new TextRepresentationAccountSpecificationVisitor().visit(
			this._context.configuration.getExistingAccountsPayable(),
		);
	}

	_doSubmit({ onDone }) {
		if (this._missesIntervals(this._getExistingAccountsPayableString()))
			this._raiseBlankErrorForContent(this._accountsPayableGroups);

		if (this._missesIntervals(this._getAccountsReceivableString()))
			this._raiseBlankErrorForContent(this._accountsReceivableGroups);

		if (this._missesDelays(this._getPayableDelays()))
			this._raiseBlankErrorForContent(this._payableDelays);

		if (this._missesDelays(this._getReceivableDelays()))
			this._raiseBlankErrorForContent(this._receivableDelays);

		onDone();
	}

	_missesIntervals(intervals) {
		return !intervals || intervals === "-";
	}
}
